import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiArrowLeft, FiCalendar } = FiIcons;

const ArticleFinancerFormation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="pt-52 pb-20 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Link
            to="/ressources"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
            Retour aux ressources
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            RNCP37275 : comment financer sa formation sans se tromper
          </h1>
          <div className="flex items-center text-gray-500 text-sm">
            <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2" />
            <span>Mis en ligne le 23/03/2026</span>
          </div>
        </motion.div>

        {/* Article content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div className="space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              Devenir formateur professionnel d'adultes via la certification RNCP37275 représente un investissement stratégique. Avec des coûts oscillant généralement entre 2 000 € et 6 000 €, la question du financement est centrale. Bonne nouvelle : plusieurs dispositifs existent, à condition de bien les mobiliser.
            </p>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Le CPF, levier principal… mais encadré
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Le Compte Personnel de Formation (CPF) reste la solution la plus utilisée. Chaque actif cumule des droits (jusqu'à 500 € par an, plafonnés à 5 000 €) mobilisables directement en ligne.
                </p>
                <p>
                  Concrètement, si la formation RNCP37275 est éligible, il est possible de la financer totalement ou partiellement sans avance de frais. Depuis les dernières réformes, un reste à charge forfaitaire peut toutefois s'appliquer.
                </p>
                <p>
                  <strong>Attention :</strong> toutes les formations ne se valent pas. Certaines sont très "digitalisées" avec peu d'accompagnement. Il est donc essentiel de vérifier la qualité pédagogique avant de mobiliser son CPF.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Les OPCO : un levier puissant pour les salariés
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Pour les salariés, les Opérateurs de Compétences (OPCO) peuvent prendre en charge tout ou partie du coût, dans le cadre du plan de développement des compétences de l'entreprise.
                </p>
                <p>La démarche est simple :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>présenter le projet à son employeur,</li>
                  <li>identifier l'OPCO de branche,</li>
                  <li>monter un dossier de financement.</li>
                </ul>
                <p>
                  Dans certains cas, l'entreprise finance intégralement la formation, notamment si elle s'inscrit dans une évolution de poste (formateur interne, montée en compétences RH, etc.).
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                France Travail : une opportunité pour les demandeurs d'emploi
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Les demandeurs d'emploi peuvent solliciter France Travail (ex-Pôle emploi) via l'AIF (Aide Individuelle à la Formation).
                </p>
                <p>Le financement dépend :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>de la cohérence du projet professionnel,</li>
                  <li>du retour à l'emploi attendu,</li>
                  <li>et des budgets disponibles localement.</li>
                </ul>
                <p>
                  Un dossier solide, argumenté autour d'un projet concret (création d'activité, insertion rapide) augmente fortement les chances d'acceptation.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Le financement personnel : un investissement à raisonner
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  De nombreux candidats choisissent également de financer tout ou partie de leur formation sur fonds propres. Dans ce cas, la logique doit être claire :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>combien vais-je générer après la certification ?</li>
                  <li>en combien de temps l'investissement est-il amorti ?</li>
                </ul>
                <p>
                  Un formateur actif peut rentabiliser sa formation en quelques missions seulement, à condition d'être accompagné sur la mise en activité.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Le bon réflexe : combiner les dispositifs
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Dans la pratique, les financements sont souvent hybrides :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>CPF + reste à charge,</li>
                  <li>OPCO + cofinancement entreprise,</li>
                  <li>France Travail + apport personnel.</li>
                </ul>
                <p>
                  C'est cette combinaison qui permet d'accéder à des formations de meilleure qualité, avec un accompagnement réel.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Au-delà du financement, la question du choix
              </h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Financer sa formation est une étape. Choisir la bonne l'est encore plus. Entre les offres low cost peu accompagnées et les parcours très coûteux, l'enjeu est de trouver un équilibre entre prix, qualité pédagogique et débouchés concrets.
                </p>
                <p>
                  C'est dans cette logique que certains acteurs, comme le groupe Advocaci à travers son Institut Supérieur des Métiers de la Formation, proposent des formats positionnés sur un juste rapport qualité-prix, avec un objectif clair : permettre non seulement d'obtenir la certification, mais surtout d'exercer rapidement le métier.
                </p>
                <p className="font-semibold text-gray-900 bg-blue-50 p-4 rounded-lg mt-4">
                  En matière de RNCP37275, le vrai financement n'est pas seulement celui que l'on obtient, mais celui que l'on est capable de rentabiliser.
                </p>
              </div>
            </div>
          </div>

          {/* Back to resources */}
          <div className="text-center pt-12">
            <Link
              to="/ressources"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
              Retour aux ressources
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ArticleFinancerFormation;
